import type { ReactNode } from 'react';

// Optional secondary action rendered as a compact square pill beside the primary
// CTA (e.g. the calendar shortcut next to "Request Time Off" in the design).
type SecondaryAction = {
  icon: ReactNode;
  /** Accessible label (rendered as aria-label + title; not shown). */
  label: string;
  onClick: (e: React.MouseEvent) => void;
};

// Call-to-action pinned to the bottom of a dashboard widget (e.g. "Request Time
// Off", "View Calendar"). Wraps itself in the mt-auto footer spacer so cards just
// render <WidgetCtaButton .../> as their last child. Per the design system the CTA
// is a full-radius neutral pill (grey fill, dark text), optionally paired with a
// square secondary button.
type WidgetCtaButtonProps = {
  icon: ReactNode;
  label: string;
  onClick: (e: React.MouseEvent) => void;
  /** When true, render the button greyed-out and non-interactive. */
  disabled?: boolean;
  /** Tooltip, useful to explain why a disabled button can't be used. */
  title?: string;
  /** Optional square pill button rendered to the right of the primary CTA. */
  secondaryAction?: SecondaryAction;
};

// Height comes from the shared control scale (lg = h-10 / 40px, replacing an invented 42px);
// the grey fill and full radius are the widget-CTA treatment above, which no Button variant
// models, so these stay raw buttons.
const pill =
  'flex h-10 items-center justify-center rounded-full bg-slate-50 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300';

export function WidgetCtaButton({
  icon,
  label,
  onClick,
  disabled,
  title,
  secondaryAction,
}: WidgetCtaButtonProps) {
  return (
    <div className="mt-auto pt-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          title={title}
          className={`${pill} flex-1 gap-2 px-4 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-slate-50`}
        >
          {icon}
          {label}
        </button>
        {secondaryAction ? (
          <button
            type="button"
            aria-label={secondaryAction.label}
            title={secondaryAction.label}
            onClick={secondaryAction.onClick}
            className={`${pill} w-10 shrink-0`}
          >
            {secondaryAction.icon}
          </button>
        ) : null}
      </div>
    </div>
  );
}
